import React, { useEffect, useState, useRef, lazy } from 'react';
import { motion, AnimatePresence, useReducedMotion, useMotionValue, useAnimationFrame } from 'framer-motion';
import { UserRound, UsersRound, ShoppingCart, Gift, Car, CalendarClock, Check } from 'lucide-react';
type Audience = 'personal' | 'team';
type GalleryCard = {
  id: string;
  category: string;
  title: string;
  quote: string;
  gradient: string;
  // Brand slug for the connector tile beneath the mockup. Every card uses a
  // real app logo here (via AppIcon) so the connector treatment is uniform
  // across the whole gallery.
  icon: string;
  ui: React.ReactNode;
};
// ── FOR YOU ────────────────────────────────────────────────────────────────
// Personal life admin. Titles are the OUTCOME (the thing that ends up done),
// not the chore. Each quote is a real, specific ask in your own words.
const PERSONAL_CARDS: GalleryCard[] = [{
  id: 'travel-booked',
  category: 'Travel',
  title: 'Trip, fully booked',
  quote: '"Book my Friday trip to SF — window seat under $400, a hotel near the office, and put it all on my calendar."',
  gradient: 'from-[#FBF4EC] to-[#F4E7D6]',
  icon: 'skyscanner',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-819" data-mp="6kdq234xr9xb">
        <div className="flex items-center justify-between mb-3" data-id="element-820" data-mp="bfktd9uamclp">
          <span className="text-[10px] font-bold text-neutral-800" data-id="element-821" data-mp="gfyoxsepju2u">
            SFO · Fri
          </span>
          <span className="text-[10px] font-bold text-green-600" data-id="element-822" data-mp="3ezxi4ytvemas">$382</span>
        </div>
        <div className="flex items-center gap-2 mb-2" data-id="element-823" data-mp="3jxqmrnfiz88y">
          <div className="h-1.5 w-6 bg-neutral-300 rounded" data-id="element-824" data-mp="3owjz3cj2st7j" />
          <div className="flex-1 h-px bg-neutral-200" data-id="element-825" data-mp="3ou7awhmq5mm1" />
          <div className="h-1.5 w-6 bg-neutral-300 rounded" data-id="element-826" data-mp="3tna9x5a9to9m" />
        </div>
        <div className="h-1.5 w-2/3 bg-neutral-100 rounded" data-id="element-827" data-mp="2w7w9x5v7hjop" />
      </div>
}, {
  id: 'errands-handled',
  category: 'Errands',
  title: 'Errands, all done',
  quote: '"Reorder my usual groceries, renew my car registration, and reschedule the dentist for next week. Tell me when each is done."',
  gradient: 'from-[#FBF1E7] to-[#FCE2CC]',
  icon: 'amazon',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 shadow-sm w-52 border border-white/40" data-id="element-828" data-mp="2p469l0eecf2b">
        <div className="flex items-center justify-between mb-3" data-id="element-829" data-mp="32v1puxr85yfx">
          <span className="text-[10px] font-bold text-neutral-800" data-id="element-830" data-mp="2xxrp4lydy0cs">
            Today's errands
          </span>
          <span className="text-[10px] font-bold text-green-600" data-id="element-831" data-mp="2a7nxlcyh1r25">3 of 3</span>
        </div>
        <div className="space-y-2" data-id="element-832" data-mp="266vmsf9ybe3r">
          {[{
        icon: ShoppingCart,
        label: 'Groceries',
        status: 'Reordered'
      }, {
        icon: Car,
        label: 'Car registration',
        status: 'Renewed'
      }, {
        icon: CalendarClock,
        label: 'Dentist',
        status: 'Rebooked'
      }].map(({
        icon: Icon,
        label,
        status
      }) => <div key={label} className="flex items-center gap-2.5 rounded-lg bg-[#FBF6F0] px-2.5 py-1.5" data-id="element-833" data-mp="3qkcexhz7q4e8">
              <span className="flex items-center justify-center w-6 h-6 rounded-lg bg-white text-[#EA580C] shrink-0 shadow-sm" data-id="element-834" data-mp="1ccldl3e3z9tm">
                <Icon size={12} strokeWidth={2.25} data-id="element-835" data-mp="1he0t6fxioo4m" />
              </span>
              <span className="text-[10px] font-semibold text-neutral-800 leading-none flex-1 truncate" data-id="element-836" data-mp="13e9ml09bya0n">
                {label}
              </span>
              <span className="flex items-center gap-1 text-[9px] font-bold text-green-600 leading-none shrink-0" data-id="element-837" data-mp="1w1lxuea1iven">
                <Check size={10} strokeWidth={3} data-id="element-838" data-mp="203qpj7gxhhyn" />
                {status}
              </span>
            </div>)}
        </div>
      </div>
}, {
  id: 'bills-paid',
  category: 'Finance',
  title: 'Bills, never late',
  quote: '"Watch my accounts, pay every recurring bill on time, and flag anything that looks off before it posts."',
  gradient: 'from-[#FBF4EC] to-[#F3E9DB]',
  icon: 'stripe',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-839" data-mp="2jqy69bxjmzfw">
        <div className="flex items-center justify-between mb-3" data-id="element-840" data-mp="v5wt25fmigll">
          <span className="text-[10px] font-bold text-neutral-800" data-id="element-841" data-mp="v3ao8dk4af72">
            This month
          </span>
          <span className="text-[10px] font-bold text-green-600" data-id="element-842" data-mp="q8n3mps8i4xr">On track</span>
        </div>
        <div className="space-y-2" data-id="element-843" data-mp="m6jsk29h92d0">
          {[true, true, true].map((paid, i) => <div key={i} className="flex items-center gap-2" data-id="element-844" data-mp="h76wcqwpuixr" data-mpi={i}>
              <div className="w-3 h-3 rounded-full bg-[#EA580C] shrink-0" data-id="element-845" data-mp="1dw4r494gugux" />
              <div className="h-1.5 flex-1 bg-neutral-100 rounded" data-id="element-846" data-mp="19u9u3gtkipmy" />
            </div>)}
        </div>
      </div>
}, {
  id: 'inbox-zero',
  category: 'Inbox',
  title: 'Inbox, cleared by morning',
  quote: '"Triage my personal inbox overnight — unsubscribe from junk, draft replies I just need to approve, and surface only what matters."',
  gradient: 'from-[#FBF1E7] to-[#FCE2CC]',
  icon: 'gmail',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-847" data-mp="1st5l6ja4qnwk">
        <div className="flex items-center justify-between mb-3" data-id="element-848" data-mp="204jyxo3zk1qt">
          <span className="text-[10px] font-bold text-neutral-800" data-id="element-849" data-mp="206ek93j65vvf">Inbox</span>
          <span className="text-[10px] font-bold text-[#EA580C]" data-id="element-850" data-mp="253wv4zgqyl62">
            2 to read
          </span>
        </div>
        <div className="space-y-2" data-id="element-851" data-mp="2a4kklgpbuty6">
          {[1, 2].map(i => <div key={i} className="flex items-center gap-2" data-id="element-852" data-mp="2e4bhm25g66b5">
              <div className="w-2 h-2 rounded-full bg-[#EA580C]/40 shrink-0" data-id="element-853" data-mp="2e1qgxggvxwez" />
              <div className="h-1.5 flex-1 bg-neutral-100 rounded" data-id="element-854" data-mp="2j2wafgiddnpk" />
            </div>)}
        </div>
      </div>
}, {
  id: 'gift-found',
  category: 'Ordering',
  title: 'The right gift, ordered',
  quote: "\"It's my mom's birthday next week — find something she'd love under $80, order it, and have it shipped to arrive on time.\"",
  gradient: 'from-[#FBF4EC] to-[#F4E7D6]',
  icon: 'target',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-855" data-mp="31vgy8jj7p2mi">
        <div className="h-16 w-full rounded-lg bg-[#FFF1E8] mb-3 flex items-center justify-center text-[#EA580C]" data-id="element-856" data-mp="36qnvb8hz792g">
          <Gift size={24} strokeWidth={1.75} data-id="element-857" data-mp="16o4wi0lqf5fe" />
        </div>
        <div className="flex items-center justify-between" data-id="element-858" data-mp="12q76ivk50fay">
          <div className="h-1.5 w-16 bg-neutral-200 rounded" data-id="element-859" data-mp="1ggu2ifyijr3c" />
          <span className="text-[10px] font-bold text-green-600" data-id="element-860" data-mp="1gidl4d7n6e8a">
            Arrives Tue
          </span>
        </div>
      </div>
}, {
  id: 'plans-made',
  category: 'Scheduling',
  title: 'Plans, locked in',
  quote: '"Find a great dinner spot for four near downtown Friday at 7, book it, and text everyone the details."',
  gradient: 'from-[#FBF1E7] to-[#FBDFC4]',
  icon: 'opentable',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-861" data-mp="1zedcpmwpjfrc">
        <div className="flex items-center justify-between mb-3" data-id="element-862" data-mp="1udz3i1jddzrq">
          <span className="text-[10px] font-bold text-neutral-800" data-id="element-863" data-mp="51x084dbj5e1">
            Fri · 7:00
          </span>
          <span className="text-[10px] font-bold text-green-600" data-id="element-864" data-mp="3lpzsj42sva">
            Table for 4
          </span>
        </div>
        <div className="h-1.5 w-full bg-neutral-100 rounded mb-1.5" data-id="element-865" data-mp="2brh5sjktjtfh" />
        <div className="h-1.5 w-2/3 bg-neutral-100 rounded" data-id="element-866" data-mp="2gn5wryirw206" />
      </div>
}];
// ── FOR YOUR TEAM ────────────────────────────────────────────────────────────
// Work outcomes a small team can't always staff for. Titles are the delivered
// result; quotes are how a teammate would actually ask in Slack.
const TEAM_CARDS: GalleryCard[] = [{
  id: 'prep-sales-call',
  category: 'Sales',
  title: 'Sales calls, prepped',
  quote: '"Prep the team for tomorrow\'s Acme call — pull the HubSpot deal, last email threads, and a one-pager on where it stands."',
  gradient: 'from-[#FBF1E7] to-[#FCE2CC]',
  icon: 'hubspot',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-867" data-mp="1o0bmtvjl0l0y">
        <div className="h-2 w-16 bg-neutral-200 rounded mb-3" data-id="element-868" data-mp="3ed0s5zt502nk" />
        <div className="flex justify-between items-center mb-2" data-id="element-869" data-mp="3ict66s1av10b">
          <span className="text-[10px] font-bold text-neutral-800" data-id="element-870" data-mp="264hha4kxl3bm">
            Acme Corp
          </span>
          <span className="text-[10px] font-bold text-green-600" data-id="element-871" data-mp="215o7on8rlwgq">$84k ARR</span>
        </div>
        <div className="h-1.5 w-full bg-neutral-100 rounded mb-1.5" data-id="element-872" data-mp="1weeifnr4ov5d" />
        <div className="h-1.5 w-3/4 bg-neutral-100 rounded" data-id="element-873" data-mp="1wcla840va2v9" />
      </div>
}, {
  id: 'board-decks',
  category: 'Finance',
  title: 'Board deck, investor-ready',
  quote: '"Refresh the board deck with the latest numbers — MRR from Stripe, burn from QuickBooks, pipeline from Salesforce."',
  gradient: 'from-[#FBF1E7] to-[#FBDFC4]',
  icon: 'stripe',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-874" data-mp="1df0kkuqa309l">
        <div className="flex justify-between items-center mb-3" data-id="element-875" data-mp="18hsc5luxmalj">
          <span className="text-[10px] font-bold text-neutral-800" data-id="element-876" data-mp="37tuuutvqf0y5">
            MRR Growth
          </span>
          <span className="text-[10px] font-bold text-green-600" data-id="element-877" data-mp="32v00qvsbdpyq">+18%</span>
        </div>
        <div className="h-16 w-full flex items-end gap-1" data-id="element-878" data-mp="3bbqu40u44aah">
          {[40, 55, 45, 70, 65, 85, 100].map((h, i) => <div key={i} className="flex-1 bg-[#EA580C]/25 rounded-t-sm" style={{
        height: `${h}%`
      }} data-id="element-879" data-mp="3fd414e8xyjiz" data-mpi={i} />)}
        </div>
      </div>
}, {
  id: 'pipeline-current',
  category: 'CRM',
  title: 'CRM, always current',
  quote: '"After every call and email, update the deal stage, log the notes, and create the follow-up tasks in Salesforce."',
  gradient: 'from-[#FBF4EC] to-[#F4E7D6]',
  icon: 'salesforce',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-880" data-mp="2wdyjnn3fac35">
        <div className="h-2 w-20 bg-neutral-800 rounded mb-3" data-id="element-881" data-mp="31dbccogz6ddn" />
        <div className="space-y-2" data-id="element-882" data-mp="36cnzzbfo7oha">
          {['Qualified', 'Proposal', 'Won'].map((_, i) => <div key={i} className="flex items-center gap-2" data-id="element-883" data-mp="36al0m0smoizw" data-mpi={i}>
              <div className="w-3 h-3 rounded-full bg-[#EA580C] shrink-0" data-id="element-884" data-mp="gmkcbe5jkopi" />
              <div className="h-1.5 flex-1 bg-neutral-100 rounded" data-id="element-885" data-mp="llx7qnahb43p" />
            </div>)}
        </div>
      </div>
}, {
  id: 'reports-shipped',
  category: 'Reporting',
  title: 'Reports, shipped on schedule',
  quote: '"Every Monday at 8am, compile last week\'s metrics into the standup doc and post the summary to #leadership."',
  gradient: 'from-[#FBF4EC] to-[#F3E9DB]',
  icon: 'slack',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-886" data-mp="323eyv1xaswox">
        <div className="flex items-center justify-between mb-3" data-id="element-887" data-mp="25dxjfv8ijwa1">
          <span className="text-[10px] font-bold text-neutral-800" data-id="element-888" data-mp="25fqsfh6lmsl7">
            Weekly report
          </span>
          <span className="text-[10px] font-bold text-green-600" data-id="element-889" data-mp="2ee2hvfvqbkc2">Sent</span>
        </div>
        <div className="h-1.5 w-full bg-neutral-100 rounded mb-1.5" data-id="element-890" data-mp="2egf6gbvzssy3" />
        <div className="h-1.5 w-5/6 bg-neutral-100 rounded mb-1.5" data-id="element-891" data-mp="2jeqfwlidl1fs" />
        <div className="h-1.5 w-2/3 bg-neutral-100 rounded" data-id="element-892" data-mp="3pu0t9qedqigg" />
      </div>
}, {
  id: 'tickets-triaged',
  category: 'Support',
  title: 'Support queue, triaged',
  quote: '"Sort the support queue — tag by urgency, draft replies for the routine ones, and escalate anything at risk of breaching SLA."',
  gradient: 'from-[#FBF1E7] to-[#FCE2CC]',
  icon: 'zendesk',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-893" data-mp="3u4ntzdostfv">
        <div className="flex items-center justify-between mb-3" data-id="element-894" data-mp="3r4ugh2iy7cfa">
          <span className="text-[10px] font-bold text-neutral-800" data-id="element-895" data-mp="3r4m3zbqqm94m">Open</span>
          <span className="text-[10px] font-bold text-[#EA580C]" data-id="element-896" data-mp="3m6km2u51sfuq">4 urgent</span>
        </div>
        <div className="space-y-2" data-id="element-897" data-mp="3h5n2cd4e3dr4">
          {[1, 2, 3].map(i => <div key={i} className="flex items-center gap-2" data-id="element-898" data-mp="3c7lny5hws2lz">
              <div className="w-2 h-2 rounded-full bg-[#EA580C]/40 shrink-0" data-id="element-899" data-mp="3c83oheblt2zp" />
              <div className="h-1.5 flex-1 bg-neutral-100 rounded" data-id="element-900" data-mp="3871mwqu7seuk" />
            </div>)}
        </div>
      </div>
}, {
  id: 'new-hires-ramped',
  category: 'Onboarding',
  title: 'New hires, ramped in week one',
  quote: '"Set up our new AE — build their onboarding doc, grant tool access, and queue intro messages to the accounts they\'re inheriting."',
  gradient: 'from-[#FBF4EC] to-[#F4E7D6]',
  icon: 'notion',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-901" data-mp="34pngs7n793lx">
        <div className="h-2 w-24 bg-neutral-800 rounded mb-4" data-id="element-902" data-mp="2qyio5pi91bl3" />
        <div className="space-y-2.5" data-id="element-903" data-mp="2uxhrj5oc10af">
          {[true, true, true].map((done, i) => <div key={i} className="flex items-center gap-2" data-id="element-904" data-mp="3io3mbv9bso2h" data-mpi={i}>
              <div className={`w-3 h-3 rounded-sm shrink-0 ${done ? 'bg-[#EA580C]' : 'border border-neutral-300'}`} data-id="element-905" data-mp="3ion2b6ff12dv" />

              <div className="h-1.5 w-full bg-neutral-200 rounded" data-id="element-906" data-mp="3nkkklia8oita" />
            </div>)}
        </div>
      </div>
}];
// Resolves a brand logo for a card's connector tile. We try two icon CDNs in
// order, and if BOTH 404 (some slugs simply aren't in either set) we render a
// brand initial badge instead of hiding the tile — so no connector container
// is ever left empty.
function AppIcon({
  slug,
  className
}: {
  slug: string;
  className?: string;
}) {
  // 0 = dashboard-icons, 1 = simpleicons, 2 = give up → initial badge
  const [stage, setStage] = useState(0);
  // Reset the resolution chain whenever the slug changes (cards swap on toggle).
  useEffect(() => {
    setStage(0);
  }, [slug]);
  const src = stage === 0 ? `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${slug}.svg` : `https://cdn.simpleicons.org/${slug.replace(/-/g, '')}`;
  if (stage >= 2) {
    return <span aria-hidden="true" className={`${className ?? ''} flex items-center justify-center font-bold text-[#EA580C] uppercase`} style={{
      fontSize: '0.7rem',
      lineHeight: 1
    }} data-id="element-907" data-mp="1bxihs9y333zo">
        {slug.charAt(0)}
      </span>;
  }
  return <img key={stage} src={src} alt="" aria-hidden="true" className={className} onError={() => setStage(s => s + 1)} data-id="element-908" data-mp="1gy65aln3t2p5" />;
}
// The warm grainy Giga texture (same image used behind the BenefitsBento
// phone). Instead of a flat peach gradient, each gallery card crops a DIFFERENT
// region of this texture for its visual backdrop, so the row reads as varied
// slices of one cohesive brand atmosphere rather than repeated gradients.
const CARD_TEXTURE = "https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Untitled_design_(6)_1.webp";
// Deterministic per-card backdrop: each card zooms WAY into the shared Giga
// texture (300–460%) at a well-spread position, so every card shows a tight,
// visibly different slice — not a similar, zoomed-out view. Position and zoom
// are seeded from independent hashes of the card id so neighbours don't rhyme.
function hashStr(id: string, seed: number) {
  let h = seed >>> 0;
  for (let i = 0; i < id.length; i++) {
    h = h * 31 + id.charCodeAt(i) >>> 0;
  }
  return h;
}
function textureStyle(id: string): React.CSSProperties {
  const x = hashStr(id, 7) % 100;
  const y = hashStr(id, 13) % 100;
  // 320% → 480%, in 40% steps, for distinctly different zoom levels per card.
  const zoom = 320 + hashStr(id, 23) % 5 * 40;
  return {
    backgroundImage: `url(${CARD_TEXTURE})`,
    backgroundSize: `${zoom}%`,
    backgroundPosition: `${x}% ${y}%`,
    backgroundRepeat: 'no-repeat'
  };
}
function GalleryJobCard({
  card,
  audience
}: {
  card: GalleryCard;
  audience: Audience;
}) {
  const isTeam = audience === 'team';
  return <div className="w-[280px] md:w-[320px] shrink-0 flex flex-col gap-6" data-id="element-909" data-mp="24n8unnkp1g0j">
      {/* Visual Area */}
      <div className={`h-[340px] rounded-3xl p-6 relative overflow-hidden flex flex-col items-center justify-center bg-gradient-to-br ${card.gradient}`} data-id="element-910" data-mp="31di4i1rca844">
        <div className="relative z-10 flex flex-col items-center" data-id="element-911" data-mp="2se4riz40b1en">
          {card.ui}
          <div className="w-px h-12 bg-black/10 my-2" data-id="element-912" data-mp="2ngcu5pklen32" />
          <div className="w-10 h-10 rounded-full bg-white/80 backdrop-blur-md shadow-sm border border-white flex items-center justify-center text-[#EA580C]" data-id="element-913" data-mp="2nhxkt43sdlvn">
            <AppIcon slug={card.icon} className="w-5 h-5 object-contain" data-id="element-914" data-mp="1bzmqoe7k09d5" />
          </div>
        </div>
        <div className="absolute inset-0 opacity-[0.03] mix-blend-overlay pointer-events-none" style={{
        backgroundImage: 'url("https://grainy-gradients.vercel.app/noise.svg")'
      }} data-id="element-915" data-mp="1div2qx6voza1" />
      </div>

      {/* Text Area */}
      <div className="flex flex-col gap-3 px-2" data-id="element-916" data-mp="14j82wftss1j3">
        <span className="flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase" data-id="element-917" data-mp="18hz3iwxh21ay">
          <span className="w-1.5 h-1.5 rounded-full bg-[#EA580C]" data-id="element-918" data-mp="ur2a53vq1g2u" />
          <span className="text-[#EA580C]" data-id="element-919" data-mp="zi29sgw86c91">{card.category}</span>
        </span>
        <h3 className="text-xl font-bold text-[#2B211A] -mt-1" data-id="element-920" data-mp="zk59xtr3dlrh">{card.title}</h3>
        <p className="text-sm text-neutral-600 leading-relaxed" data-id="element-921" data-mp="kuqzsjfoz0b1">{card.quote}</p>
      </div>
    </div>;
}
function CardMarquee({
  cards,
  audience,
  reduceMotion
}: {
  cards: GalleryCard[];
  audience: Audience;
  reduceMotion: boolean | null;
}) {
  if (reduceMotion) {
    return <div className="flex overflow-x-auto gap-6 pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-922" data-mp="gqj7kca0z282">
        {cards.map(card => <GalleryJobCard key={card.id} card={card} audience={audience} data-id="element-923" data-mp="3tbf42mwcahvf" />)}
      </div>;
  }
  return <DraggableMarquee cards={cards} audience={audience} data-id="element-924" data-mp="3odn5ja16vxi9" />;
}
// Auto-advancing AND draggable marquee. The track auto-scrolls continuously;
// the user can grab and drag it left/right at any point, and it resumes
// auto-scrolling from wherever they release. Three identical copies of the card
// set are rendered so there's always content buffering both sides during a
// drag, and the x position is wrapped into the middle band every frame for a
// seamless infinite loop.
function DraggableMarquee({
  cards,
  audience
}: {
  cards: GalleryCard[];
  audience: Audience;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const draggingRef = useRef(false);
  // Width of ONE card set (the track holds three identical sets).
  const [setWidth, setSetWidth] = useState(0);
  // Auto-scroll speed (px/sec). Faster on mobile, where the user is more
  // likely to just watch the cards drift by than grab and drag them.
  const [speed, setSpeed] = useState(50);
  // Measure a single set's width, then park x in the middle copy so there's a
  // full set of cards buffering both the left and right during drags.
  useEffect(() => {
    const measure = () => {
      const el = trackRef.current;
      if (!el) return;
      const w = el.scrollWidth / 3;
      setSetWidth(w);
      x.set(-w);
      setSpeed(window.innerWidth < 640 ? 95 : 55);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [cards, x]);
  // Wrap x into the middle band so the loop stays seamless and always filled.
  const wrapMid = (v: number) => {
    if (!setWidth) return v;
    const mod = ((v + 2 * setWidth) % setWidth + setWidth) % setWidth;
    return -2 * setWidth + mod;
  };
  useAnimationFrame((_, delta) => {
    if (draggingRef.current || !setWidth) return;
    // Move the track left→right (x increasing); wrapMid keeps it seamless.
    const next = x.get() + speed * delta / 1000;
    x.set(wrapMid(next));
  });
  return (
    // No edge fade on mobile (the strong left fade looked heavy on narrow
    // screens) — it reads as a clean carousel there. The soft side fades only
    // kick in from the sm breakpoint up.
    <div className="relative w-full overflow-hidden sm:[mask-image:linear-gradient(to_right,transparent,black_6%,black_94%,transparent)]" data-id="element-925" data-mp="daq0z50wbgah">
      <motion.div ref={trackRef} className="flex gap-6 w-max cursor-grab active:cursor-grabbing select-none touch-pan-y" style={{
        x
      }} drag="x" dragMomentum={false} dragElastic={0.04} onDragStart={() => {
        draggingRef.current = true;
      }} onDragEnd={() => {
        // Snap back into the seamless middle band, then let the auto-scroll
        // frame loop take over again from this position.
        x.set(wrapMid(x.get()));
        draggingRef.current = false;
      }} data-id="element-926" data-mp="8iykxofd8syl">
        {[...cards, ...cards, ...cards].map((card, i) => <GalleryJobCard key={`${card.id}-${i}`} card={card} audience={audience} data-id="element-927" data-mp="2akquce5regfo" data-mpi={i} />)}
      </motion.div>
    </div>
  );
}
// Personal ("At home") audience is hidden for now: for companies evaluating
// Giga, a work/home toggle raised the question of employees using it for
// personal matters. The personal cards and toggle are kept intact below —
// flip this to true to bring the audience switch back.
const SHOW_PERSONAL_AUDIENCE = false;
export function AudienceSwitch({
  headline = <>
      Your <span className="text-[#EA580C]" data-id="element-928" data-mp="1mvedcs46x42t">everything</span> agent.
    </>
}: {
  headline?: React.ReactNode;
}) {
  const reduceMotion = useReducedMotion();
  const [audience, setAudience] = useState<Audience>('team');
  const cards = SHOW_PERSONAL_AUDIENCE && audience === 'personal' ? PERSONAL_CARDS : TEAM_CARDS;
  return <section className="w-full bg-[#FAF8F4] py-24 md:py-32 border-b border-neutral-200/70 relative overflow-hidden" data-id="element-929" data-mp="13ymv8m0dwv0c">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 relative" data-id="element-930" data-mp="tv7tk5z7wgih">
        {/* Section opening */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1]
      }} className="mb-10 md:mb-12 max-w-3xl mx-auto text-center" data-id="element-931" data-mp="yt9lzhij9s6m">
          <h2 className="text-3xl md:text-5xl font-title text-[#2B211A] tracking-tight leading-[1.1] font-[500]" data-id="element-932" data-mp="b5jgjrvbnkre">
            Your  <span className="text-[#EA580C]" data-id="element-933" data-mp="g172lyeibxso">everything</span> agent
          </h2>
          {!SHOW_PERSONAL_AUDIENCE && <p className="mt-4 text-base md:text-lg text-neutral-600 leading-relaxed" data-id="element-934" data-mp="l0jpgj5dp4v9">
              Fifteen AIs that each do a little?
              <br className="md:hidden" data-id="element-935" data-mp="3ojwi84wfa3zr" />
              {' '}Or one that can do it all.
            </p>}
        </motion.div>

        {/* Audience toggle (hidden while the personal audience is off) */}
        {SHOW_PERSONAL_AUDIENCE && <div className="flex justify-center mb-12 md:mb-16" data-id="element-936" data-mp="3lg74468yeeuz">
          <div role="tablist" aria-label="Choose audience" className="relative grid grid-cols-2 gap-1 p-1.5 w-full max-w-[360px] rounded-full border border-neutral-200/70 bg-white shadow-sm" data-id="element-937" data-mp="3giyly5nt2atq">
            <motion.div aria-hidden="true" className="absolute inset-y-1.5 w-[calc(50%-6px)] rounded-full bg-[#FAF8F4] border border-neutral-200/80 shadow-sm" animate={{
            left: audience === 'team' ? '6px' : 'calc(50%)'
          }} transition={{
            type: 'spring',
            stiffness: 400,
            damping: 30
          }} data-id="element-938" data-mp="3q8i75hyzqgrr" />

            <button role="tab" aria-selected={audience === 'team'} onClick={() => setAudience('team')} className={`relative z-10 flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors duration-300 ${audience === 'team' ? 'text-[#2B211A]' : 'text-neutral-500 hover:text-[#2B211A]'}`} data-id="element-939" data-mp="3ciemgvfrev4f">
              <UsersRound size={16} strokeWidth={2.5} className={audience === 'team' ? 'text-[#EA580C]' : 'text-neutral-400'} data-id="element-940" data-mp="ub0s7g81rfqb" />

              <span className="whitespace-nowrap" data-id="element-941" data-mp="zadqcx4dc987">At work</span>
            </button>
            <button role="tab" aria-selected={audience === 'personal'} onClick={() => setAudience('personal')} className={`relative z-10 flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors duration-300 ${audience === 'personal' ? 'text-[#2B211A]' : 'text-neutral-500 hover:text-[#2B211A]'}`} data-id="element-942" data-mp="lam8glaqe8ox">
              <UserRound size={16} strokeWidth={2.5} className={audience === 'personal' ? 'text-[#EA580C]' : 'text-neutral-400'} data-id="element-943" data-mp="1i03l5k84sv04" />

              <span className="whitespace-nowrap" data-id="element-944" data-mp="147du31dqwzw5">At home</span>
            </button>
          </div>
        </div>}

        {/* Separate carousel per audience */}
        <div className="w-full overflow-hidden" data-id="element-945" data-mp="1vyth2wj7somw">
          <AnimatePresence mode="wait" data-id="element-946" data-mp="20qupg7d9hv7n">
            <motion.div key={audience} initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -8
          }} transition={{
            duration: 0.35,
            ease: [0.16, 1, 0.3, 1]
          }} data-id="element-947" data-mp="20rwejfr44i8d">
              <CardMarquee cards={cards} audience={audience} reduceMotion={reduceMotion} data-id="element-948" data-mp="36oevxvinp2r" />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>;
}